import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import {
  Badge, Button, Card, Input, Spinner,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from '@agion/ui'
import { Server, Plus, Search } from 'lucide-react'
import { CloudProviderIcon } from './InfraCloudProviderIcon'
import { DeploymentModelBadge } from './InfraDeploymentModelBadge'
import { listEnvironments } from './infra_environment.service'
import type { CloudEnvironment, EnvironmentStatus } from '@/core/infra_types'
import { formatRelativeTime } from '@/core/infra_utils'

const statusVariantMap: Record<EnvironmentStatus, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  pending: 'default',
  provisioning: 'info',
  active: 'success',
  updating: 'info',
  failed: 'error',
  decommissioning: 'warning',
  decommissioned: 'default',
}

export function EnvironmentsPage() {
  const { t } = useTranslation(['pages'])
  const navigate = useNavigate()
  const [environments, setEnvironments] = useState<CloudEnvironment[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [providerFilter, setProviderFilter] = useState<string>('all')

  const loadEnvironments = useCallback(async () => {
    try {
      setIsLoading(true)
      const params: Record<string, string | number> = { perPage: 100 }
      if (statusFilter && statusFilter !== 'all') params['status'] = statusFilter
      if (providerFilter && providerFilter !== 'all') params['cloud_provider'] = providerFilter

      const result = await listEnvironments(params as Parameters<typeof listEnvironments>[0])
      setEnvironments(Array.isArray(result.data) ? result.data : [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load environments')
    } finally {
      setIsLoading(false)
    }
  }, [statusFilter, providerFilter])

  useEffect(() => {
    void loadEnvironments()
  }, [loadEnvironments])

  const filtered = environments.filter(env =>
    env.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    env.region.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div className="container-standard">
      {/* Page Header */}
      <div className="page-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Server className="text-blue-600" size={28} />
            <div>
              <h1 className="page-title">{t('pages:environments.title', 'Environments')}</h1>
              <p className="page-description">
                {t('pages:environments.description', 'Manage cloud environments across providers')}
              </p>
            </div>
          </div>
          <Button variant="primary" onClick={() => navigate('/environments/new')}>
            <Plus size={16} className="mr-1" />
            {t('pages:environments.create', 'Create Environment')}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder={t('pages:environments.search', 'Search environments...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder={t('pages:environments.filters.allStatuses', 'All Statuses')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('pages:environments.filters.allStatuses', 'All Statuses')}</SelectItem>
            <SelectItem value="active">{t('pages:environments.status.active', 'Active')}</SelectItem>
            <SelectItem value="provisioning">{t('pages:environments.status.provisioning', 'Provisioning')}</SelectItem>
            <SelectItem value="failed">{t('pages:environments.status.failed', 'Failed')}</SelectItem>
            <SelectItem value="decommissioned">{t('pages:environments.status.decommissioned', 'Decommissioned')}</SelectItem>
          </SelectContent>
        </Select>

        <Select value={providerFilter} onValueChange={setProviderFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder={t('pages:environments.filters.allProviders', 'All Providers')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('pages:environments.filters.allProviders', 'All Providers')}</SelectItem>
            <SelectItem value="gcp">{t('pages:providers.gcp')}</SelectItem>
            <SelectItem value="azure">{t('pages:providers.azure')}</SelectItem>
            <SelectItem value="aws">{t('pages:providers.aws')}</SelectItem>
            <SelectItem value="upcloud">{t('pages:providers.upcloud')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : error ? (
        <Card>
          <div className="p-8 text-center text-red-600">{error}</div>
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <div className="p-8 text-center text-slate-500 dark:text-slate-400">
            {searchQuery
              ? t('pages:environments.noResults', 'No environments match your search.')
              : t('pages:environments.empty', 'No environments yet. Create one to get started.')}
          </div>
        </Card>
      ) : (
        <Table variant="modern">
          <TableHeader variant="modern">
            <TableRow variant="modern">
              <TableHead variant="modern">{t('pages:environments.columns.name', 'Name')}</TableHead>
              <TableHead variant="modern">{t('pages:environments.columns.provider', 'Provider')}</TableHead>
              <TableHead variant="modern">{t('pages:environments.columns.region', 'Region')}</TableHead>
              <TableHead variant="modern">{t('pages:environments.columns.model', 'Deployment')}</TableHead>
              <TableHead variant="modern">{t('pages:environments.columns.status', 'Status')}</TableHead>
              <TableHead variant="modern">{t('pages:environments.columns.updated', 'Updated')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody variant="modern">
            {filtered.map(env => (
              <TableRow
                key={env.id}
                variant="modern"
                className="cursor-pointer"
                onClick={() => navigate(`/environments/${env.id}`)}
              >
                <TableCell className="font-medium">{env.name}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <CloudProviderIcon provider={env.cloudProvider} size={16} />
                    <span className="text-slate-600 dark:text-slate-400">{env.cloudProvider === 'none' ? t('pages:providers.onPremise') : env.cloudProvider.toUpperCase()}</span>
                  </div>
                </TableCell>
                <TableCell className="text-slate-500">{env.region}</TableCell>
                <TableCell><DeploymentModelBadge model={env.deploymentModel} /></TableCell>
                <TableCell>
                  <Badge variant={statusVariantMap[env.status] ?? 'default'}>
                    {t(`pages:environments.status.${env.status}`, env.status)}
                  </Badge>
                </TableCell>
                <TableCell className="text-slate-400 text-xs">{formatRelativeTime(env.updatedAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  )
}
